import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { all, select, take, fork, delay, put, call, race, takeEvery, takeLatest } from 'redux-saga/effects';
import ddt from '@wix/da-ddt';
import { flagEnabled } from '@wix/da-react-context/pkg/flags/redux/selectors';
import { requestPuppy, isApiError } from '@wix/da-http-client';
import { appMounted } from '../app/actions';
import { setPushSubscriptionState, togglePushSubscription, registerPushSubscription, unregisterPushSubscription, initPushSubscriptions, initPushSubscriptionsSuccess, updatePushSubscriptions, displayPushNotificationsModal } from './actions';
import { PushSubscriptionState } from './types';
import { getIsMobile, getPublicSession } from '../../publicSession/selectors';
import { confirmNo, confirmYes, pushModal, popModal, pushGlobalConfirmationModal } from '../../Modals/redux/actionCreators';
import { ModalType } from '../../Modals/redux/types';
import { getArePushSubscriptionsInitialized, getPushSubscriptionState, getPushSubscriptions } from './selectors';
import { isiOS } from '../../utils/browser';
let registration = null;
function* registerPush(endpoint_url, public_key, auth_secret) {
    const params = {
        endpoint_url,
        public_key,
        auth_secret
    };
    const result = yield call(requestPuppy, {
        method: 'post',
        url: '/push/register',
        data: params
    });
    if (!result || isApiError(result)) {
        return false;
    }
    return result.success;
}
function* unregisterPush() {
    yield call(requestPuppy, {
        method: 'post',
        url: '/push/unregister'
    });
}
function askNotificationPermission() {
    return new Promise(function(resolve, reject) {
        const permissionResult = Notification.requestPermission(function(result) {
            resolve(result);
        });
        try {
            permissionResult.then(resolve, reject);
        } catch (error) {
            if (error instanceof TypeError) {
                // Safari uses a deprecated callback version of Notification.requestPermission
                // https://developer.mozilla.org/en-US/docs/Web/API/Notification/requestPermission_static#syntax
                Notification.requestPermission(resolve);
            } else {
                ddt.log('pwa', 'Error thrown for push notifications permission');
                throw error;
            }
        }
    });
}
function* subscribeToPush() {
    if (!registration) {
        return;
    }
    const session = yield select(getPublicSession);
    try {
        var _json_keys, _json_keys1;
        const subscription = yield registration.pushManager.subscribe({
            applicationServerKey: session.push.vapidPublicKey,
            userVisibleOnly: true
        });
        const json = subscription.toJSON();
        var _json_endpoint, _json_keys_p256dh, _json_keys_auth;
        const subscribed = yield call(registerPush, (_json_endpoint = json.endpoint) !== null && _json_endpoint !== void 0 ? _json_endpoint : '', (_json_keys_p256dh = (_json_keys = json.keys) === null || _json_keys === void 0 ? void 0 : _json_keys.p256dh) !== null && _json_keys_p256dh !== void 0 ? _json_keys_p256dh : '', (_json_keys_auth = (_json_keys1 = json.keys) === null || _json_keys1 === void 0 ? void 0 : _json_keys1.auth) !== null && _json_keys_auth !== void 0 ? _json_keys_auth : '');
        if (subscribed) {
            yield put(setPushSubscriptionState(PushSubscriptionState.SUBSCRIBED));
            ddt.log('pwa', 'Subscribed successfully to push notifications');
        } else {
            yield put(setPushSubscriptionState(PushSubscriptionState.FAILED));
        }
    } catch (err) {
        ddt.error('pwa', 'Failed to subscribe to push notifications', err);
        yield put(setPushSubscriptionState(PushSubscriptionState.FAILED));
    }
}
function* initPushSubscriptionState() {
    try {
        if (!registration || typeof window === 'undefined') {
            return;
        }
        // IOS only has this when installed to the homescreen
        if (!('Notification' in window)) {
            yield put(setPushSubscriptionState(PushSubscriptionState.UNABLE_TO_SUBSCRIBE));
            return;
        }
        if (window.Notification.permission === 'denied') {
            yield put(setPushSubscriptionState(PushSubscriptionState.UNABLE_TO_SUBSCRIBE));
            ddt.log('pwa', 'Init cancelled due to permission denied');
            return;
        }
        if (window.Notification.permission === 'default') {
            yield put(setPushSubscriptionState(PushSubscriptionState.READY_TO_SUBSCRIBE));
            return;
        }
        if (window.Notification.permission !== 'granted') {
            return;
        }
        // If the device subscription doesn't match backend record
        // unsubscribe and, if necessary, resubscribe again.
        const subscription = yield registration.pushManager.getSubscription();
        const session = yield select(getPublicSession);
        if (subscription) {
            if (subscription.endpoint === session.push.endpoint) {
                yield put(setPushSubscriptionState(PushSubscriptionState.SUBSCRIBED));
            } else {
                ddt.log('pwa', 'Resubscribing to push notifications');
                yield subscription.unsubscribe();
                yield call(subscribeToPush);
            }
        } else {
            if (session.push.endpoint) {
                yield call(unregisterPush);
            }
            yield put(setPushSubscriptionState(PushSubscriptionState.READY_TO_SUBSCRIBE));
        }
    } catch (e) {
        ddt.error('pwa', 'Failed to init push', e);
    }
}
function* showConfirmationModal() {
    // Confirm Subscription
    yield put(pushGlobalConfirmationModal({
        titleTransKey: 'pwa.confirmationPopup.title',
        textTransKey: 'pwa.confirmationPopup.text',
        confirmBtnLabelTransKey: 'pwa.confirmationPopup.button.cta',
        cancelBtnLabelTransKey: 'pwa.confirmationPopup.button.cancel'
    }));
    const { yes } = yield race({
        yes: take(confirmYes),
        no: take(confirmNo)
    });
    yield put(popModal());
    return yes;
}
function* showPushNotificationsModal() {
    const pwa = yield select(flagEnabled, 'pwa');
    if (!pwa) {
        return;
    }
    const initialized = yield select(getArePushSubscriptionsInitialized);
    if (!initialized) {
        yield put(initPushSubscriptions());
    }
    yield put(pushModal(ModalType.PUSH_NOTIFICATIONS, {
        wrapInModal: false
    }));
}
function* checkIfPushSubscriptionsReady() {
    if (!registration) {
        return false;
    }
    const state = yield select(getPushSubscriptionState);
    if ([
        PushSubscriptionState.LOADING,
        PushSubscriptionState.FAILED,
        PushSubscriptionState.UNABLE_TO_SUBSCRIBE
    ].includes(state)) {
        ddt.log('pwa', 'push subscriptions not ready', state);
        return false;
    }
    return true;
}
function* handleRegisterPush(action) {
    try {
        const isReady = checkIfPushSubscriptionsReady();
        if (!isReady) {
            return;
        }
        // If the user has enabled push notifications, just show the modal
        const state = yield select(getPushSubscriptionState);
        // we only care about READY_TO_SUBSCRIBE after this point
        if (state !== PushSubscriptionState.READY_TO_SUBSCRIBE) {
            ddt.log('pwa', 'push subscriptions state not READY_TO_SUBSCRIBE');
            return;
        }
        yield put(setPushSubscriptionState(PushSubscriptionState.LOADING));
        // start subscribing
        if (Notification.permission === 'default') {
            // we prompt the user with a confirmation modal first to avoid accidental
            // push notifications blocking
            const confirmed = yield call(showConfirmationModal);
            if (!confirmed) {
                yield put(setPushSubscriptionState(PushSubscriptionState.READY_TO_SUBSCRIBE));
                return;
            }
            const permission = yield call(askNotificationPermission);
            if (permission !== 'granted') {
                yield put(setPushSubscriptionState(PushSubscriptionState.UNABLE_TO_SUBSCRIBE));
                ddt.log('pwa', 'Permission denied for push notifications');
                return;
            }
        }
        // actually subscribe to the push notifications
        yield call(subscribeToPush);
    } catch (e) {
        ddt.error('pwa', 'Failed to register push', e);
    }
}
function* handleUnregisterPush(action) {
    try {
        var _registration_pushManager;
        const isReady = checkIfPushSubscriptionsReady();
        if (!isReady) {
            return;
        }
        // Only unsubscribe if the state is SUBSCRIBED
        const state = yield select(getPushSubscriptionState);
        if (state !== PushSubscriptionState.SUBSCRIBED) {
            ddt.log('pwa', 'exiting unregister due to state not SUBSCRIBED');
            return;
        }
        yield put(setPushSubscriptionState(PushSubscriptionState.LOADING));
        const subscription = yield registration === null || registration === void 0 ? void 0 : (_registration_pushManager = registration.pushManager) === null || _registration_pushManager === void 0 ? void 0 : _registration_pushManager.getSubscription();
        if (subscription) {
            ddt.log('pwa', 'unregistering push subscriptions');
            // unsubscribe
            yield subscription.unsubscribe();
            yield call(unregisterPush);
        }
        yield put(setPushSubscriptionState(PushSubscriptionState.READY_TO_SUBSCRIBE));
        ddt.log('pwa', 'successfully unregistered push subscriptions');
    } catch (e) {
        ddt.error('pwa', 'Failed to unregister push', e);
    }
}
function* handleTogglePush(action) {
    const state = yield select(getPushSubscriptionState);
    const isReady = checkIfPushSubscriptionsReady();
    if (!isReady) {
        return;
    }
    if (state === PushSubscriptionState.SUBSCRIBED) {
        ddt.log('pwa', 'toggling push subscriptions off');
        yield put(unregisterPushSubscription());
    } else {
        ddt.log('pwa', 'toggling push subscriptions on');
        yield put(registerPushSubscription());
    }
}
function* handleInitPushSubscriptions(action) {
    const result = yield call(requestPuppy, {
        method: 'get',
        url: '/push/settings'
    });
    if (!result || isApiError(result)) {
        ddt.error('pwa', 'Failed to fetch push subscriptions');
        return false;
    }
    ddt.log('pwa', 'push subscription settings initialized', result.send);
    yield put(initPushSubscriptionsSuccess(result.send));
}
function* handleUpdatePushSubscriptions(action) {
    yield delay(1000); // + takeLatest = debounced
    const { payload } = action;
    const subscriptions = yield select(getPushSubscriptions);
    const { note, commentReply, watcher, mention, favourite, favouriteMilestone } = _object_spread({}, subscriptions, payload);
    const data = {
        send: {
            note,
            watcher,
            mention,
            favourite,
            comment_reply: commentReply,
            favourite_milestone: favouriteMilestone
        }
    };
    const result = yield call(requestPuppy, {
        method: 'post',
        url: '/push/settings/update',
        data
    });
    if (!result || isApiError(result)) {
        ddt.error('pwa', 'Failed to update notification subscriptions');
        return false;
    } else {
        ddt.log('pwa', 'Updated notifications settings', result);
    }
}
function* handleAppMounted() {
    // wait for mount before doing anything
    yield take(appMounted);
    if (typeof window === 'undefined') {
        return;
    }
    try {
        const pwa = yield select(flagEnabled, 'pwa');
        const pwa_desktop = yield select(flagEnabled, 'pwa_desktop');
        const isMobile = yield select(getIsMobile);
        const shouldPWA = pwa && (isMobile || isiOS() || pwa_desktop);
        // Give the page some time to settle
        yield delay(1000);
        if ('serviceWorker' in navigator) {
            if (shouldPWA) {
                ddt.log('pwa', 'Registering PWA service worker');
                registration = yield call([
                    navigator.serviceWorker,
                    'register'
                ], '/sw.js', {
                    scope: '/'
                });
                yield call(initPushSubscriptionState);
            } else {
                navigator.serviceWorker.getRegistrations().then(function(registrations) {
                    for (const reg of registrations){
                        var _reg_active, _reg_active1;
                        ddt.log('pwa', 'Found service worker', (_reg_active = reg.active) === null || _reg_active === void 0 ? void 0 : _reg_active.scriptURL);
                        if ((_reg_active1 = reg.active) === null || _reg_active1 === void 0 ? void 0 : _reg_active1.scriptURL) {
                            var _reg_active2;
                            const url = new URL((_reg_active2 = reg.active) === null || _reg_active2 === void 0 ? void 0 : _reg_active2.scriptURL);
                            if (url.pathname === '/sw.js') {
                                var _reg_active3;
                                ddt.log('pwa', 'Unregistering service worker', (_reg_active3 = reg.active) === null || _reg_active3 === void 0 ? void 0 : _reg_active3.scriptURL);
                                reg.unregister();
                            }
                        }
                    }
                });
            }
        } else {
            yield put(setPushSubscriptionState(PushSubscriptionState.UNABLE_TO_SUBSCRIBE));
            ddt.log('pwa', 'Service worker API is not supported');
        }
    } catch (e) {
        ddt.error('pwa', 'Failed to PWA', e);
    }
}
export default function* saga() {
    yield all([
        fork(handleAppMounted),
        takeLatest(togglePushSubscription, handleTogglePush),
        takeLatest(registerPushSubscription, handleRegisterPush),
        takeLatest(unregisterPushSubscription, handleUnregisterPush),
        takeEvery(initPushSubscriptions, handleInitPushSubscriptions),
        takeEvery(displayPushNotificationsModal, showPushNotificationsModal),
        takeLatest(updatePushSubscriptions, handleUpdatePushSubscriptions)
    ]);
}

//# sourceMappingURL=saga.js.map